import { useState } from 'react';
import { Box, InputLabel } from '@mui/material';
import { format, parseISO, isValid, Locale } from 'date-fns';
import queryStringLib from 'query-string';
import { getDateLocale } from './dateLocale';
import { NavigateFunction, Location } from 'react-router-dom';
import { SttFormText } from '../sporttia/forms/SttFormText';
import { SttFormTextarea } from '../sporttia/forms/SttFormTextarea';
import { SttFormSelect } from '../sporttia/forms/SttFormSelect';
import { SttFormDate } from '../sporttia/forms/SttFormDate';
import { SttFormCheck } from '../sporttia/forms/SttFormCheck';
import { getPath } from '../pages/Pages';
import { isErrorResponse, isObject } from '../types/common';
import latinizeCharacters from './latinizeCharacters';
import constants from '../config/constants';
import i18n from '../i18n';
import { translatePlaceholders } from './translatePlaceholders';

interface FormatPeriodOptions {
    dateFormat?: string;
    timeFormat?: string;
    dateLabel?: string;
    periodLabel?: string;
    timeLabel?: string;
    scheduleLabel?: string;
}

/**
 * Helper to parse a date value to Date object
 * Returns null if the value is invalid
 */
export function parseToDate(
    value: string | Date | null | undefined,
): Date | null {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return isValid(value) ? value : null;
    }
    // Try to parse as ISO string, fallback to new Date for other formats
    const parsed = parseISO(value);
    if (isValid(parsed)) {
        return parsed;
    }
    const fallback = new Date(value);
    return isValid(fallback) ? fallback : null;
}

/**
 * Format a ini,end pair as "date1 - date2", "date, time1 : time2" etc.
 */
export function formatPeriod(
    ini: string | Date | null | undefined,
    end: string | Date | null | undefined,
    options?: FormatPeriodOptions,
    translationFunc?: (key: string) => string,
): [string, string, string, string, string] {
    const defaultOptions: FormatPeriodOptions = {
        dateFormat: 'dd/MM/yyyy',
        timeFormat: 'HH:mm',
        dateLabel: translationFunc ? translationFunc('Date') : 'Fecha',
        periodLabel: translationFunc ? translationFunc('Period') : 'Periodo',
        timeLabel: translationFunc ? translationFunc('Time') : 'Hora',
        scheduleLabel: translationFunc ? translationFunc('Horary') : 'Horario',
    };

    const myOptions = options
        ? { ...defaultOptions, ...options }
        : defaultOptions;

    const locale = getDateLocale();
    const iniDate = parseToDate(ini);
    const endDate = parseToDate(end);

    // Return empty strings if dates are invalid
    if (!iniDate || !endDate) {
        return ['', '', '', '', ''];
    }

    const dateIni = format(iniDate, myOptions.dateFormat!, { locale });
    const dateEnd = format(endDate, myOptions.dateFormat!, { locale });
    const timeIni = format(iniDate, myOptions.timeFormat!, { locale });
    const timeEnd = format(endDate, myOptions.timeFormat!, { locale });

    let dateString = '';
    let timeString;
    let dateLabel = myOptions.periodLabel || '';
    let timeLabel = myOptions.scheduleLabel || '';

    if (dateIni !== dateEnd) {
        dateString = `${dateIni} - ${dateEnd}`;
    } else {
        dateString = dateIni;
        dateLabel = myOptions.dateLabel || '';
    }

    if (timeIni !== timeEnd) {
        timeString = `${timeIni} - ${timeEnd}`;
    } else {
        timeString = timeIni;
        timeLabel = myOptions.scheduleLabel || '';
    }

    const dateTime = `${dateIni} ${timeIni} - ${dateEnd} ${timeEnd}`;

    return [dateString, timeString, dateLabel, timeLabel, dateTime];
}

/**
 * Safely formats a date string or Date object.
 * Returns the formatted string or a fallback value if the date is invalid.
 */
export function safeFormatDate(
    value: string | Date | null | undefined,
    formatStr: string,
    options?: { locale?: Locale; fallback?: string },
): string {
    const fallback = options?.fallback ?? '';
    const date = parseToDate(value);
    if (!date) {
        return fallback;
    }
    return format(date, formatStr, { locale: options?.locale });
}

/**
 * Format price
 */
export const formatPrice = (num: number, currencySymbol = '€'): string => {
    const result = (Math.round(num * 100) / 100).toFixed(2);
    return `${Number.isNaN(Number(result)) ? ' - ' : result} ${currencySymbol}`;
};

/**
 * Format price by locale
 */
export const formatPriceByLocale = (
    num = 0,
    currency = 'EUR',
    locale = 'es-ES',
): string =>
    Number(num).toLocaleString(locale, {
        style: 'currency',
        currency,
    });

/**
 * Parse a user typed price into a regular JS number.
 *
 * The price inputs emit the raw text typed by the user, so with a Spanish
 * locale the decimal separator arrives as a comma ("5,45"). Both separators
 * must be accepted. Returns null when the value is empty or not numeric so
 * callers can tell "no price" apart from a legit 0.
 */
export const parsePriceToNumber = (
    price: string | number | null | undefined,
): number | null => {
    if (price === undefined || price === null || price === '') {
        return null;
    }

    const parsedPrice = parseFloat(price.toString().trim().replace(',', '.'));

    return Number.isFinite(parsedPrice) ? parsedPrice : null;
};

/**
 * Parse price into a regular JS float with 2 decimals
 */
export const parsePrice = (
    price: string | number | null | undefined,
): string | null => {
    const parsedPrice = parsePriceToNumber(price);

    return parsedPrice === null ? null : parsedPrice.toFixed(2);
};

/**
 * Format a price for an API payload: a number rounded to 2 decimals.
 * Falls back to 0 for empty or non numeric values.
 */
export const formatPriceForApi = (
    price: string | number | null | undefined,
): number => {
    const parsedPrice = parsePriceToNumber(price);

    return parsedPrice === null ? 0 : Math.round(parsedPrice * 100) / 100;
};

/**
 * It's define
 */
export const isDefined = <T,>(val: T | null | undefined): val is T =>
    val !== null;

interface BuildQueryStringOptions {
    includeEmpty?: boolean;
}

/**
 * Construir un query string
 *
 * Devuelve un string con lo que enviar a una llamada de tipo GET del tipo a=1&b=2&c=82. Tener en cuenta
 * que no devuelve el inicio "?".
 *
 * @param {Object} params - Objeto con los parámetros
 * @param {Object} options - Opciones:
 *
 *  - bool includeEmpty - Incluye a los que el valor esté vacío o es undefined.
 *
 * @return string - Query string sin "?"
 */
export const buildQueryString = (
    params: Record<string, unknown>,
    options: BuildQueryStringOptions = {},
): string =>
    Object.entries(params)
        .filter(([, value]) => options.includeEmpty || value !== undefined)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');

interface UpdateElementOptions {
    key?: string;
    merge?: boolean;
}

/**
 * Update element in array
 *
 * This function create as well if the element doesn't exist.
 */
export function updateElementInArray<T extends Record<string, unknown>>(
    arr: T[] | null | undefined,
    element: T,
    { key = 'id', merge = false }: UpdateElementOptions = {},
): T[] {
    if (element) {
        const newArray = arr ? [...arr] : [];
        const foundElementIndex = newArray.findIndex(
            (e: any) => e[key] === element[key],
        );

        // Update it
        if (foundElementIndex > -1) {
            newArray[foundElementIndex] = merge
                ? { ...newArray[foundElementIndex], ...element }
                : element;

            // Create a new one
        } else {
            newArray.push(element);
        }

        return newArray;
    }
    return arr || [];
}

/**
 * Delete element from array
 */
export function deleteElementFromArray<T extends { id: unknown }>(
    arr: T[] | null | undefined,
    element: T,
): T[] | undefined {
    if (arr) {
        const newArray = [...arr];
        newArray.splice(
            newArray.findIndex((e: any) => e.id === element.id),
            1,
        );
        return newArray;
    }
    return undefined;
}

/**
 * Sub set object
 *
 *    * Object obj
 *    * Array|String fields
 */
export function subsetObject<T extends Record<string, unknown>>(
    obj: T,
    fields: string[] | string,
): Partial<T> {
    let myFields = fields;
    if (typeof myFields === 'string') {
        myFields = myFields.split(',');
    }

    const newObj: Partial<T> = {};
    myFields.forEach((field: string) => {
        if (obj[field] !== undefined) {
            (newObj as Record<string, unknown>)[field] = obj[field];
        }
    });

    return newObj;
}

/**
 * Normalize duration
 *
 * Given duration > 60 turns it into <hours>h <minutes>m
 */
export function normalizeDuration(duration: number): string {
    // Crear cadena xh ym
    if (duration > 60) {
        const hours = Math.floor(duration / 60);
        const minutes = duration % 60;

        return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
    }
    return `${duration}m`;
}

interface FormItem {
    id: number;
    name: string;
    type: string | null;
    answer?: {
        value: string | number;
    };
    options?: Array<{
        name: string;
        value: string;
    }>;
}

interface OnChangeParams {
    name: string | undefined;
    value: unknown;
}

// Generate 'question' fields for the activity/event forms
export function generateFormField(
    item: FormItem,
    onChange: (params: OnChangeParams) => void,
    placeholder = false,
): React.ReactElement | null {
    let field: React.ReactElement | null;

    switch (item.type) {
        case 'NUMERIC':
            field = (
                <SttFormText
                    name={item.id.toString()}
                    caption={item.name}
                    defVal={item.answer ? String(item.answer.value) : ''}
                    onChange={onChange}
                />
            );
            break;
        case 'STRING':
            field = (
                <>
                    {placeholder && (
                        <>
                            <InputLabel>{item.name}</InputLabel>
                            <Box sx={{ mb: 1 }} />
                        </>
                    )}

                    <SttFormTextarea
                        name={item.id.toString()}
                        caption={item.name}
                        defVal={item.answer ? String(item.answer.value) : ''}
                        onChange={onChange}
                        placeholder={placeholder}
                    />
                </>
            );
            break;
        case 'SELECT':
            field = (
                <SttFormSelect
                    name={item.id.toString()}
                    caption={item.name}
                    defVal={item.answer ? item.answer.value : ''}
                    options={item?.options?.map((opt: any) => ({
                        caption: opt.name,
                        value: opt.value,
                    }))}
                    onChange={onChange}
                />
            );
            break;
        case 'DATE':
            field = (
                <SttFormDate
                    name={item.id.toString()}
                    caption={item.name}
                    defVal={item.answer ? String(item.answer.value) : ''}
                    onChange={onChange}
                />
            );
            break;
        case 'CHECK':
            field = (
                <SttFormCheck
                    name={item.id.toString()}
                    caption={item.name}
                    checked={item.answer ? !!item.answer.value : false}
                    onChange={({ name, value }: any) =>
                        onChange({ name, value: +value })
                    }
                />
            );
            break;
        default:
            field = (
                <SttFormTextarea
                    name={item.id.toString()}
                    caption={item.name}
                    defVal={item.answer ? String(item.answer.value) : ''}
                    onChange={onChange}
                />
            );
    }

    return field;
}

interface UserObject {
    mship?: {
        fullName?: string;
        name?: string;
        surname1?: string;
        surname2?: string;
    };
    displayName?: string;
    fullName?: string;
    name?: string;
    surname1?: string;
    surname2?: string;
}

// Given a possibly user object, return its mship.fullName property, its fullName property, or the combination of name, surname1 and surname2 if applicable.
export function fullName(userObj: UserObject | null | undefined): string {
    if (!userObj) {
        return '';
    }

    if (userObj.mship) {
        if (userObj.mship?.fullName) {
            return userObj.mship.fullName.split('null').join('');
        }
        if (
            userObj.mship?.name &&
            userObj.mship?.surname1 &&
            userObj.mship?.surname2
        ) {
            return `${userObj.mship.name} ${userObj.mship.surname1} ${userObj.mship.surname2}`
                ?.split('null')
                .join('');
        }
    }

    if (userObj.displayName) {
        return userObj.displayName.split('null').join('');
    }

    return (
        userObj.fullName?.split('null').join('') ||
        (
            (userObj.name || '') +
            (userObj.surname1 ? ` ${userObj.surname1}` : '') +
            (userObj.surname2 ? ` ${userObj.surname2}` : '')
        )
            ?.split('null')
            .join('')
    );
}

// Return an array of values as a comma-separated string, optinally using extractorFunc() to get the values
export function commaSeparated<T>(
    array: T[] | null | undefined,
    extractorFunc?: (val: T) => string,
): string | null {
    if (!Array.isArray(array)) {
        return null;
    }

    return array.reduce(
        (res, val, i) =>
            res +
            (i > 0 ? ',' : '') +
            (extractorFunc ? extractorFunc(val) : String(val)),
        '',
    );
}

// Esta función intenta hacer un truqui para cargar el documento en un iframe e imprimir desde allí, pero no funciona (cross-origin)
// x-frame-options no sirve ya que solo acepta DENY y SAMEORIGIN (no es el caso)
// No sirve ni con un dataurl ni con url normal. ni siquiera está claro que sea posible mandar desde javascript algo a la cola de impresión
// Lo dejo aquí para que nuestro yo del futuro no intente implementar de nuevo una cosa imposible.
export function print(url: string): void {
    fetch(url)
        .then((response: any) => response.blob())
        .then(
            (blob) =>
                new Promise<string>((resolve) => {
                    const fr = new FileReader();
                    fr.onload = () => {
                        resolve(fr.result as string);
                    };
                    fr.readAsDataURL(blob);
                }),
        )
        .then((dataUrl) => {
            let iframe: HTMLIFrameElement | null =
                document.createElement('iframe');
            document.body.appendChild(iframe);

            iframe.style.display = 'none';
            iframe.onload = function handleOnLoad() {
                // print frame 10ms after load
                setTimeout(() => {
                    try {
                        iframe?.focus();
                        iframe?.contentWindow?.print();
                    } catch (err) {
                        console.log(err);
                    }
                }, 10);
                // kill frame 20ms after load
                setTimeout(() => {
                    iframe?.blur();
                    if (iframe) {
                        document.body.removeChild(iframe);
                        iframe = null;
                    }
                }, 20);
            };

            iframe.src = dataUrl;
        });
}

// Copiar value al portapapeles
export function copyToClipboard(
    value: string,
    callback?: (value: string) => void,
): void {
    navigator.clipboard
        .writeText(value)
        .then(() => typeof callback === 'function' && callback(value));
}

/**
 * The returned object is created with [`query-string`](https://www.npmjs.com/package/query-string).
 */
export function getQueryStrings(
    string: string,
    options: queryStringLib.ParseOptions = {},
): queryStringLib.ParsedQuery<string> {
    return queryStringLib.parse(string, options);
}

/**
 * Abre una pestaña con la url pasada como parámetro.
 * @param url
 */
export function openNewBrowserTab(url: string): void {
    const win = window.open(url, '_blank');
    win?.focus();
}

/**
 * Devuelve un array con solo los valores obtenidos a través de la clave pasada como parámetro.
 * @param key Clave de la que se va a sacar el valor.
 * @param objects Array con los objetos.
 * @returns {[]} Array con los valores.
 */
export function getArrayWithOnlyValues<
    T extends Record<string, unknown>,
    K extends keyof T,
>(key: K, objects: T[]): T[K][] {
    const data: T[K][] = [];

    objects.forEach((item: any) => {
        if (key in item) data.push(item[key]);
    });

    return data;
}

/**
 * Realiza el proceso de redireccionar al login y luego redireccionar de nuevo a la página desde
 * donde se inició el redireccionamiento a login.
 * @param navigate useNavigate de la página donde nos encontremos.
 * @param location useLocation de la página donde nos encontremos.
 */
export function redirectLogin(
    navigate: NavigateFunction,
    location: Location,
): void {
    navigate(getPath('login'), {
        state: { from: location },
    });
}

/**
 * Comprueba si un @login tiene el string #OFF#taltal que indica que el usuario se dio de baja
 * @param string
 * @returns {boolean}
 */
export function checkOffTag(string: string): boolean {
    let result = false;
    if (string.includes('#OFF#')) {
        result = true;
    }
    return result;
}

/**
 * Devuelve un objeto al que se le eliminan las claves dadas por "keys".
 * @param object Objecto a modificar.
 * @param keys Lista de claves a eliminar.
 * @returns {*}
 */
export const objectWithoutKeys = <T extends Record<string, unknown>>(
    object: T,
    keys: string[] = [],
): T => {
    const newObject = { ...object };

    keys.map((key: any) => (newObject[key] ? delete newObject[key] : null));

    return newObject;
};

/**
 * Compara dos objetos si son iguales.
 * @param obj1 Objeto 1.
 * @param obj2 Objecto 2.
 * @returns {boolean} Devuelve true si son iguales y false en otro caso.
 */
export const checkObjEqual = (
    obj1: Record<string, unknown>,
    obj2: Record<string, unknown>,
): boolean => {
    const obj1Length = Object.keys(obj1).length;
    const obj2Length = Object.keys(obj2).length;

    if (obj1Length === obj2Length) {
        return Object.keys(obj1).every(
            (key: any) => key in obj2 && obj2[key] === obj1[key],
        );
    }
    return false;
};

/**
 * Open an external url in a new tab, simulating a link click.
 * @param url URL to open.
 */
export const openUrl = (url: string): void => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_new';
    link.click();
};

/**
 * Formats seconds to a string of hours: minutes: seconds.
 * @param duration Seconds.
 * @returns {string} Format string.
 */
export const secondsToFormatTime = (duration: number): string => {
    // Hours, minutes and seconds

    const hrs = ~~(duration / 3600);

    const mins = ~~((duration % 3600) / 60);

    const secs = ~~duration % 60;

    // Output like "1:01" or "4:03:59" or "123:03:59"
    let ret = '';

    if (hrs > 0) {
        ret += `${hrs}:${mins < 10 ? '0' : ''}`;
    }

    ret += `${mins}:${secs < 10 ? '0' : ''}`;
    ret += `${secs}`;
    return ret;
};

export function shallowEqual(
    object1: Record<string, unknown> | null | undefined,
    object2: Record<string, unknown> | null | undefined,
): boolean {
    if (object1 && object2) {
        const keys1 = Object.keys(object1);
        const keys2 = Object.keys(object2);

        if (keys1.length !== keys2.length) {
            return false;
        }

        for (const key of keys1) {
            if (
                typeof object1[key] === 'object' &&
                typeof object2[key] === 'object'
            ) {
                if (
                    !shallowEqual(
                        object1[key] as Record<string, unknown>,
                        object2[key] as Record<string, unknown>,
                    )
                ) {
                    return false;
                }
            } else if (object1[key] !== object2[key]) {
                return false;
            }
        }

        return true;
    }
    return false;
}

interface ValidationError {
    field: string;
    message: string;
}

interface ErrorWithValidation {
    msg?: string;
    errors?: ValidationError[];
}

export function showErrors(
    error: ErrorWithValidation | null | undefined,
    showMessage: (type: string, message: string) => void,
): void {
    function validationErrors() {
        if (error?.errors && error.errors.length > 0) {
            let errorsFormatted = '';
            error.errors.forEach((err: any) => {
                errorsFormatted += `Field: ${err.field} - ${err.message}\n`;
            });
            showMessage('E', errorsFormatted);
        }
    }

    if (error) {
        // Temporal filter until http error codes are forwarded to individual components
        if (error.msg === '[[Se ha producido un error de validación.]]') {
            validationErrors();
        }
    }
}

/**
 * Generic http(s) request that can be self-aborted after a specified timeout.
 * @param method
 * @param uri
 * @param params
 * @param https
 * @param timeoutMillisecs
 * @returns {Promise<unknown>}
 */
export const requestAdHoc = <T = unknown,>(
    method: string,
    url: string,
    params: Record<string, unknown> = {},
): Promise<T> => {
    const headers: Record<string, string> = {
        Connection: 'keep-alive',
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json',
        'X-SOURCE': 'Sporttia',
    };

    const METHOD = method.toUpperCase();
    let queryString = '';

    if (METHOD === 'GET' && params) {
        if (Object.keys(params).length > 0) {
            // filter null and undefined param values
            const filteredParams = Object.keys(params).reduce(
                (result, param) =>
                    params[param] !== undefined && params[param] !== null
                        ? {
                              ...result,
                              [param]: params[param],
                          }
                        : result,
                {} as Record<string, unknown>,
            );

            // create the querystring
            queryString += Object.keys(filteredParams).reduce(
                (result, param, i) =>
                    `${result + (i > 0 ? '&' : '?') + param}=${params[param]}`,
                '',
            );
        }
    }

    return new Promise((resolve: any, reject: any) => {
        fetch(url + queryString, {
            ...{
                headers,
                method,
            },
            ...((METHOD === 'POST' || METHOD === 'PUT') &&
                params && { body: JSON.stringify(params) }),
        })
            .then((response: any) => {
                if (response.status === 200) {
                    try {
                        response.json().then(resolve);
                    } catch (error) {
                        reject({
                            error: {
                                msg: error,
                            },
                        });
                    }
                } else {
                    reject(`Error ${response.status}`);
                }
            })
            .catch((err: Error) => {
                // Not even a status code

                reject({
                    error: {
                        msg:
                            err?.message || 'Error de conexión sin especificar',
                        log: JSON.stringify(err),
                    },
                });
            });
    });
};

export const getErrorMessage = (error: unknown): string => {
    const raw =
        (isObject(error) &&
            typeof (error as { message?: unknown }).message === 'string' &&
            (error as { message: string }).message) ||
        (isErrorResponse(error) && error.error.msg) ||
        'Error';
    // 869d58e58: el backend envuelve claves i18n no resueltas en [[...]]
    // (a veces con args, e.g. `[[api.item.badRequest.noStock: 0 Unidades]]`).
    // Resolverlas aquí garantiza que cualquier toast disparado por el
    // handler de error por defecto de cxt.api(...) muestra texto legible.
    return translatePlaceholders(raw, i18n.t.bind(i18n));
};

export const getObjectWithoutEmptyStrings = <T extends Record<string, unknown>>(
    obj: T,
): Partial<T> =>
    Object.entries(obj)
        .filter(
            ([, value]) => !(typeof value === 'string' && value.trim() === ''),
        )
        .reduce(
            (acc, [key, value]) => ({ ...acc, [key]: value }),
            {} as Partial<T>,
        );

interface FormField {
    id: number | string;
    answer?: {
        value: string | number;
    };
}

export const composeFormData = (
    form: FormField[],
): Record<string | number, string | number> =>
    form.reduce(
        (result: any, field: any) => {
            const fieldValue = field.answer ? field.answer.value : '';
            return { ...result, [field.id]: fieldValue };
        },
        {} as Record<string | number, string | number>,
    );

export const latinize = (string: string): string =>
    string.replace(
        /[^A-Za-z0-9]/g,
        (a) => latinizeCharacters[a as keyof typeof latinizeCharacters] || a,
    );

/*
        Función para recoger en un objeto los valores que hayan cambiado con respecto al objeto de izquierda
    */
export const getObjectDiff = <T extends Record<string, unknown>>(
    left: T,
    right: T,
): Partial<T> => {
    const diffObjectKeys = Object.keys(left).filter((leftKey: any) => {
        if (left[leftKey] !== undefined) {
            if (right[leftKey] === undefined) return true;
            return right[leftKey] !== left[leftKey];
        }
        return false;
    });
    return diffObjectKeys.reduce(
        (diff, key) => ({ ...diff, [key]: left[key] }),
        {} as Partial<T>,
    );
};

/*
        Función para recoger en un objeto los valores que hayan cambiado con respecto al objeto de izquierda
    */
export const createObjectWithUpdatedFieldsOnly = <
    T extends Record<string, unknown>,
>(
    left: T,
    right?: T,
): Partial<T> | T => {
    if (right) {
        return getObjectDiff(left, right);
    }

    return left;
};

interface PrevAndUpdatedStates<T> {
    setPrevState: React.Dispatch<React.SetStateAction<T>>;
    setUpdatedState: React.Dispatch<React.SetStateAction<Partial<T>>>;
    prevState: T;
    updatedState: Partial<T>;
}

export const usePrevAndUpdatedStates = <T extends Record<string, unknown>>(
    item: T,
): PrevAndUpdatedStates<T> => {
    const [prevState, setPrevState] = useState<T>(item);
    const [updatedState, setUpdatedState] = useState<Partial<T>>({});

    return {
        setPrevState,
        setUpdatedState,
        prevState,
        updatedState,
    };
};

/**
 * Comprueba si la forma de pago requiere una pasarela de pago.
 * Algunos pagos no se confirman de inmediato, sino después de pasar por la pasarela de pago.
 * La confirmación del pago se recibe, por lo general, en el tpv confirmation, cuando el banco notifica.
 * @param paymentForm Forma de pago.
 * @returns {boolean}
 */
export const isPaymentFormWithGateway = (paymentForm: string): boolean =>
    paymentForm === constants.payment.paymentForms.tpv.name ||
    paymentForm === constants.payment.paymentForms.zitycard.name;
